// components/Checkout/index.tsx
import { useState } from 'react';
import {
  FaCheckCircle,
  FaArrowLeft,
  FaLock,
  FaTruck,
  FaShieldAlt,
  FaUndo,
} from 'react-icons/fa';
import {  processBMLPayment } from './bmlPayment';
import { bankDetails, copyToClipboard } from './bankTransfer';
import { ShippingForm } from './ShippingForm';
import { PaymentMethods } from './PaymentMethods';
import { OrderReview } from './OrderReview';
import { OrderConfirmation } from './OrderConfirmation';

interface CheckoutProps {
  isOpen: boolean;
  onClose: () => void;
  items: any[];
  total: number;
  onComplete: (orderData: any) => void;
}

export default function Checkout({ isOpen, onClose, items, total, onComplete }: CheckoutProps) {
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const [orderData, setOrderData] = useState<any>(null);

  const [orderId, setOrderId] = useState<string | null>(null);

const createOrderId = () => {
  return `ORD-${Date.now()}`;
};

const handleProceedToPayment = () => {
  if (!orderId) {
    setOrderId(createOrderId());
  }
  setStep(2);
};

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    bankAccount: '',
    reference: '',
  });

  if (!isOpen) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    const orderId = 'ORD-' + Math.random().toString(36).substring(2, 10).toUpperCase();


    const usdToMvrRate = 20.00;
    const mvrTotal = Number((total * usdToMvrRate).toFixed(2)); // 154.20 MVR
    const amountInLaari = Math.round(mvrTotal * 100); // 15420

    try {
      let paymentResult = null;
      const bmlResponse = await processBMLPayment(amountInLaari, orderId, formData);

      // Process based on payment method
      if (paymentMethod === 'bml') {
        // Redirect to BML gateway
        if (bmlResponse.redirectUrl) {
          // Save order data before redirect
          const order = createOrderObject(orderId, 'pending', paymentMethod, null, formData);
          saveOrder(order);
          // Redirect to BML gateway
          window.location.href = bmlResponse.redirectUrl;
          return;
        }
        paymentResult = bmlResponse;
      } else if (paymentMethod === 'bank') {
        paymentResult = { success: true, reference: bankDetails.reference };
      } else if (paymentMethod === 'cod') {
        paymentResult = { success: true, method: 'Cash on Delivery' };
      } else if (paymentMethod === 'card') {
        paymentResult = { success: true, transactionId: 'CARD-' + Date.now() };
      }

    //   const order = createOrderObject(orderId, getOrderStatus(), paymentMethod, paymentResult, formData);

      const order = createOrderObject(
  orderId,
  "pending_payment",
  paymentMethod,
  {
    transactionId: bmlResponse.transactionId,
    state: bmlResponse.state,
  },
  formData
);

      
      saveOrder(order);
      setOrderData(order);

      setIsProcessing(false);
      setIsComplete(true);
      onComplete(order);
    } catch (error) {
      console.error('Payment error:', error);
      setIsProcessing(false);
      alert('Payment failed. Please try again.');
    }
  };

  const createOrderObject = (orderId: string, status: string, method: string, paymentResult: any, data: any) => ({
    id: Date.now().toString(),
    orderId: orderId,
    items: items,
    total: total,
    status: status,
    paymentMethod: method,
    paymentDetails: paymentResult,
    date: new Date().toISOString(),
    customer: data,
  });

  const getOrderStatus = () => {
  switch (paymentMethod) {
    case 'cod':
      return 'pending_payment';
    case 'bank':
      return 'pending';
    case 'bml':
      return 'pending_payment';
    default:
      return 'confirmed';
  }
};

  const saveOrder = (order: any) => {
    const existingOrders = JSON.parse(localStorage.getItem('shopOrders') || '[]');
    existingOrders.push(order);
    localStorage.setItem('shopOrders', JSON.stringify(existingOrders));
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  const handleCopy = (text: string) => {
    copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  if (isComplete) {
    return (
      <OrderConfirmation
        paymentMethod={paymentMethod}
        bankDetails={bankDetails}
        orderId={orderData?.orderId}
        onContinue={handleClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm overflow-y-auto" onClick={handleClose}>
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="sticky top-0 bg-white z-10 p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                  <FaArrowLeft className="text-gray-500" />
                </button>
                <h2 className="font-primary text-xl font-bold text-gray-800">Checkout</h2>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className={`font-medium ${step >= 1 ? 'text-blue-600' : ''}`}>1</span>
                <span className="w-8 h-0.5 bg-gray-300"></span>
                <span className={`font-medium ${step >= 2 ? 'text-blue-600' : ''}`}>2</span>
                <span className="w-8 h-0.5 bg-gray-300"></span>
                <span className={`font-medium ${step >= 3 ? 'text-blue-600' : ''}`}>3</span>
              </div>
            </div>
          </div>

          <div className="p-6">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Shipping Information */}
              {step === 1 && (
                <ShippingForm
                  formData={formData}
                  onInputChange={handleInputChange}
                  onNext={handleProceedToPayment}
                />
              )}

              {/* Step 2: Payment Method */}
              {step === 2 && (
                <PaymentMethods
                    orderId={orderId}
                  paymentMethod={paymentMethod}
                  onPaymentMethodChange={setPaymentMethod}
                  showBankDetails={showBankDetails}
                  onToggleBankDetails={() => setShowBankDetails(!showBankDetails)}
                  copied={copied}
                  onCopy={handleCopy}
                  bankDetails={bankDetails}
                  formData={formData}
                  onInputChange={handleInputChange}
                  onBack={() => setStep(1)}
                  onNext={() => setStep(3)}
                />
              )}

              {/* Step 3: Review & Confirm */}
              {step === 3 && (
                <OrderReview
                  items={items}
                  total={total}
                  paymentMethod={paymentMethod}
                  isProcessing={isProcessing}
                  onBack={() => setStep(2)}
                  onSubmit={handleSubmit}
                />
              )}
            </form>

            {/* Trust Badges */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><FaTruck /> Free Shipping</span>
                <span className="flex items-center gap-1"><FaShieldAlt /> Secure Checkout</span>
                <span className="flex items-center gap-1"><FaUndo /> 30-Day Returns</span>
                <span className="flex items-center gap-1"><FaLock /> SSL Encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}